import { normaliseStreetLine, normaliseText } from './normalise';

/**
 * The mailing form of an address, and the folds that decide whether two
 * writings of one address are the same address (piece 27; decision 0406).
 *
 * 🔒 Every document, label, envelope and export prints the mailing form: line
 * one, line two, then the mailing city, the state and the postal code. The
 * municipality is informational and reaches no template — 114 Springer Lane is
 * mailed to Aliquippa and stands in Hopewell Township, and the postal service
 * delivers to the first name only (product owner, 2026-09-15).
 */
export interface MailingAddressParts {
  countryCode: string;
  addressLines: readonly string[];
  dependentLocality?: string | null;
  /** The mailing city — the only city a document prints. */
  locality?: string | null;
  /** The town, township or borough the building stands in; never printed. */
  municipality?: string | null;
  administrativeArea?: string | null;
  postalCode?: string | null;
}

/** Civil-division words a person leaves off: Hopewell and Hopewell Township are one place. */
const CIVIL_DIVISIONS: readonly string[] = ['township', 'twp', 'borough', 'boro', 'town', 'village', 'city of', 'municipality of'];

/**
 * Case, accents, punctuation and every street abbreviation folded to one
 * spelling. 🔨 One fold, held in `normalise.ts`: the duplicate tier asks
 * whether two typed addresses name one building and the verifier asks whether
 * a provider's answer standardised anything, and a second table would let the
 * two answers drift. A provider that answers Lane where a person typed Ln has
 * standardised nothing, so the two fold together and the answer stays Verified.
 */
export function foldStreetText(value: string): string {
  return normaliseStreetLine(value);
}

/** The same fold, then the civil-division word dropped, so a typed place name matches the provider's fuller one. */
export function foldPlaceName(value: string): string {
  let text = normaliseText(value);
  for (const word of CIVIL_DIVISIONS) {
    if (text.endsWith(` ${word}`)) text = text.slice(0, -(word.length + 1)).trim();
    if (text.startsWith(`${word} `)) text = text.slice(word.length + 1).trim();
  }
  return text;
}

/** Two street lines that differ only in case, punctuation or a street abbreviation. */
export function sameStreetText(left: string | null | undefined, right: string | null | undefined): boolean {
  return foldStreetText(left ?? '') === foldStreetText(right ?? '');
}

/** Two place names that differ only in case, punctuation or a civil-division word. */
export function samePlaceName(left: string | null | undefined, right: string | null | undefined): boolean {
  const a = foldPlaceName(left ?? '');
  const b = foldPlaceName(right ?? '');
  return a !== '' && a === b;
}

/**
 * One piece of a mailing line: the part of the address it draws, or null for the
 * comma and the space between parts, and the text. A street piece carries its
 * line's index among the street lines as they were stored.
 */
export interface MailingAddressSegment {
  part: 'addressLines' | 'dependentLocality' | 'locality' | 'administrativeArea' | 'postalCode' | 'countryCode' | null;
  text: string;
  streetLine?: number;
}

/**
 * The mailing address as lines of parts (T563): the one composition the lines,
 * the block and the one-line form are drawn from, so a screen that marks one
 * part of a line draws the same lines a document prints.
 */
export function formatMailingAddressSegments(address: MailingAddressParts, homeCountryCode = 'US'): MailingAddressSegment[][] {
  const lines: MailingAddressSegment[][] = [];
  address.addressLines.forEach((line, streetLine) => {
    const text = line.trim();
    if (text) lines.push([{ part: 'addressLines', text, streetLine }]);
  });
  const district = address.dependentLocality?.trim();
  if (district) lines.push([{ part: 'dependentLocality', text: district }]);
  const city = address.locality?.trim() ?? '';
  const region = address.administrativeArea?.trim() ?? '';
  const postal = address.postalCode?.trim() ?? '';
  const tail: MailingAddressSegment[] = [];
  if (city) tail.push({ part: 'locality', text: city });
  if (city && region) tail.push({ part: null, text: ', ' });
  if (region) tail.push({ part: 'administrativeArea', text: region });
  if (postal && tail.length > 0) tail.push({ part: null, text: ' ' });
  if (postal) tail.push({ part: 'postalCode', text: postal });
  if (tail.length > 0) lines.push(tail);
  const country = address.countryCode.trim().toUpperCase();
  if (country && country !== homeCountryCode.trim().toUpperCase()) lines.push([{ part: 'countryCode', text: country }]);
  return lines;
}

/**
 * The mailing address as lines: the street lines, then the district where a
 * country uses one, then the city, the state and the postal code on one line,
 * then the country where it is not the reader's own. The municipality is
 * absent by construction — no caller can print it through here.
 */
export function formatMailingAddressLines(address: MailingAddressParts, homeCountryCode = 'US'): string[] {
  return formatMailingAddressSegments(address, homeCountryCode).map((line) => line.map((segment) => segment.text).join(''));
}

/** The same block as one string, newline separated, for a region that renders text. */
export function formatMailingAddress(address: MailingAddressParts, homeCountryCode = 'US'): string {
  return formatMailingAddressLines(address, homeCountryCode).join('\n');
}

/**
 * The same block on one line, for a tile, a list row or a maps link with no
 * room for a block. 🔨 One composition serves all three shapes, so a line and a
 * block cannot come to disagree about what a document prints. 💰 The block
 * formatter had no caller at all and the one-line form was composed separately
 * beside it (found in review, 2026-09-15).
 */
export function formatMailingAddressOneLine(address: MailingAddressParts, homeCountryCode = 'US'): string {
  return formatMailingAddressLines(address, homeCountryCode).join(', ');
}

/** The edit distance between two strings: how many single-character changes turn one into the other. */
export function editDistance(left: string, right: string): number {
  if (left === right) return 0;
  let previous = Array.from({ length: right.length + 1 }, (_, i) => i);
  for (let i = 1; i <= left.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= right.length; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      current[j] = Math.min((current[j - 1] ?? 0) + 1, (previous[j] ?? 0) + 1, (previous[j - 1] ?? 0) + cost);
    }
    previous = current;
  }
  return previous[right.length] ?? Math.max(left.length, right.length);
}

/**
 * Two place names one typing apart, which is a misspelling of one name rather
 * than two different places. Tolado and Toledo are one town typed twice;
 * Aliquippa and Hopewell Township are two names for one building, and telling
 * those apart is what decides whether a city difference is a correction.
 */
export function looksMisspelt(typed: string | null | undefined, held: string | null | undefined): boolean {
  const a = foldPlaceName(typed ?? '');
  const b = foldPlaceName(held ?? '');
  if (!a || !b || a === b) return false;
  // 🔨 A name that starts differently is a different name, whatever the edit distance says.
  if (a.slice(0, 2) !== b.slice(0, 2)) return false;
  const shortest = Math.min(a.length, b.length);
  const distance = editDistance(a, b);
  /**
   * 🔨 A short name tolerates one edit, and only across vowels. 💰 One edit
   * inside eight characters was called a typo, so Newton and Newtown, Wilson
   * and Wilton were each one town (found in review, 2026-09-15). A vowel moved
   * is how a place name is mistyped or misheard — Tolado for Toledo, Hopwell
   * for Hopewell — and a consonant changed makes another real town.
   */
  if (shortest < 8) return distance === 1 && isVowelEdit(a, b);
  return distance <= 2 && distance * 2 < shortest;
}

const VOWELS = 'aeiou';

/** The characters one edit touched: what is left of each name once the shared start and the shared end are taken off. */
export function editedCharacters(left: string, right: string): string {
  let start = 0;
  while (start < left.length && start < right.length && left[start] === right[start]) start += 1;
  let end = 0;
  while (end < left.length - start && end < right.length - start && left[left.length - 1 - end] === right[right.length - 1 - end]) end += 1;
  return left.slice(start, left.length - end) + right.slice(start, right.length - end);
}

/** True where every character the one edit touched is a vowel. */
function isVowelEdit(left: string, right: string): boolean {
  const edited = editedCharacters(left, right);
  return edited !== '' && [...edited].every((character) => VOWELS.includes(character));
}

/** Which city name a person typed, measured against the two the provider holds. */
export type CityMatch = 'mailing' | 'municipality' | 'misspelling' | 'neither';

export interface ProviderPlaceNames {
  /** What the provider gives as the postal or mailing city, where it gives one. */
  mailingCity?: string | null;
  /** The town, township or borough the provider places the building in. */
  municipality?: string | null;
}

/**
 * 🔨 A typed mailing city is never overwritten by a municipality. A person who
 * typed Aliquippa keeps Aliquippa; a person who typed Hopewell Township typed
 * the municipality, so the mailing city fills in beside it and the municipality
 * lands in its own part. Anything else is a real difference in the city.
 */
export function cityMatch(typed: string | null | undefined, names: ProviderPlaceNames): CityMatch {
  const value = (typed ?? '').trim();
  if (!value) return 'neither';
  if (samePlaceName(value, names.mailingCity)) return 'mailing';
  if (samePlaceName(value, names.municipality)) return 'municipality';
  if (looksMisspelt(value, names.mailingCity) || looksMisspelt(value, names.municipality)) return 'misspelling';
  return 'neither';
}

export interface ResolvedCity {
  /** What the address's city part becomes: the mailing city, always. */
  locality: string | null;
  /** What lands beside it, or null where the two names are one name. */
  municipality: string | null;
  /** True where the provider's names say nothing about the typed city, so it is a real difference. */
  differs: boolean;
}

/**
 * The mailing-city rule (decisions 0406 and 0672). A typed mailing city is
 * kept; a typed municipality moves aside and the mailing city fills in; a typed
 * city the provider's postal city disagrees with is a difference the person is
 * asked about; and a typed city a provider holding no postal city cannot place
 * is kept, with the municipality beside it.
 */
export function resolveCity(typed: string | null | undefined, names: ProviderPlaceNames): ResolvedCity {
  const mailing = names.mailingCity?.trim() || null;
  const municipality = names.municipality?.trim() || null;
  const beside = municipality && (!mailing || !samePlaceName(municipality, mailing)) ? municipality : null;
  const asTyped = (typed ?? '').trim() || null;
  switch (cityMatch(typed, names)) {
    case 'mailing':
      // The typed name is the mailing city, so it stays exactly as typed.
      return { locality: asTyped ?? mailing, municipality: beside, differs: false };
    case 'municipality':
      // The typed name is the municipality, so the mailing city fills in and the typed name lands beside it.
      return { locality: mailing ?? asTyped, municipality: beside ?? asTyped, differs: false };
    case 'misspelling':
      /**
       * One name typed twice, so the provider's spelling is offered as a
       * correction. 💰 A typo near the municipality put the municipality in the
       * city's own part and dropped the mailing city: Hopwell answered Hopewell
       * Township where the postal service delivers to Aliquippa (found in
       * review, 2026-09-15). The mailing city is what a document prints, so it
       * wins here as it wins everywhere else.
       */
      return { locality: mailing ?? municipality ?? asTyped, municipality: beside, differs: true };
    default:
      /**
       * 🔨 A typed city is kept wherever the provider names no postal city of
       * its own (decision 0672, reversing 0406's amendment). *"Hopewell for
       * Aliquippa is our rule, not the map's"* — product owner, 2026-09-23. A map
       * service that knows only Hopewell Township says where the building
       * stands, not where the post goes, so the township lands beside the typed
       * city and never over it. 💰 The amendment offered the township as the
       * city, and accepting it replaced Aliquippa on the owner's own address.
       */
      if (!mailing && asTyped) return { locality: asTyped, municipality: beside, differs: false };
      /**
       * A postal city the provider does name is the one correction a city takes:
       * one mailing city offered for another, which a person sees and answers.
       * A blank city is offered the one name the provider holds, since nothing
       * typed is there to keep.
       */
      return { locality: mailing ?? municipality ?? asTyped, municipality: beside, differs: true };
  }
}
